import type {
  FactoryMatch,
  FactoryMatchOption,
  KeyPair,
  OptionValue,
} from "@views/Upholstery/types"
import type { PdmAssetRecord } from "../../../types"
import type { PdmFlatRow } from "../../../pdm/normalizePdm"
import {
  extractDigitalAssetDetails,
  extractFactoryMatchAttributes,
  formatSeatDiagramSchematicLabel,
  getSeatAndRowSlices,
  resolveFactoryMatchKitDisplayValue,
  unwrapValue,
  FACTORY_MATCH_PATTERN_DEFAULT,
  FACTORY_MATCH_TONE_DEFAULT,
} from "../../../pdm/normalizePdm"
import type { SeatDiagramLabelContext } from "../../../constants/seat-diagram-schematic-labels"
// PDM image fallback (commented out — Factory Match uses Dato images only)
// import { buildFactoryMatchOptionImage } from "../../../constants/factory-match-local-assets"
import { resolveFactoryMatchPerforationForVehicle } from "../../../constants/factory-match-perforation-overrides"
import {
  FACTORY_MATCH_MIDDLE_KEYPAIR_KEY,
  FACTORY_MATCH_TRIM_KEYPAIR_KEY,
  type FactoryMatchFilterConfig,
  type FactoryMatchVariation,
  resolveFactoryMatchFilterConfig,
} from "../../factory/factory-match-filter"
import {
  canonicalizeFactoryMatchOptionTitle,
  normalizeFactoryMatchTitleKey,
  resolveFactoryMatchOptionTitle,
  sortFactoryMatchOptionsByDisplayOrder,
} from "../../factory/factory-match-option-order"
import {
  type FactoryMatchProductContext,
  type FactoryMatchSlugMaps,
  analyzeFactoryMatchVariation,
  resolveFactoryMatchCardFilterSlug,
  resolveRowConfigDisplayLabel,
} from "./factoryMatchSlugs"
import {
  // buildPdmImageUrl, // PDM digital-assets URLs disabled — Dato images only
  lookupDatoFactoryMatchImage,
  lookupDatoSchematicImageBySku,
  resolveDatoMatchValueForFactoryMatchOption,
  resolveFactoryMatchOptionRawValue,
  resolveFactoryMatchOptionValue,
} from "./factoryMatchImages"

/**
 * If we ever have to pick one SKU from a list, prefer the longer part number.
 * Factory Match cards no longer merge different SKUs; this is kept for
 * inspect/debug helpers that still need a single representative.
 */
export function pickPreferredFactoryMatchPartNumber(
  partNumbers: string[]
): string {
  const unique = [
    ...new Set(
      partNumbers.map((pn) => pn.replace(/\s+/g, "").trim()).filter(Boolean)
    ),
  ]
  if (!unique.length) return ""
  return [...unique].sort(
    (a, b) => b.length - a.length || a.localeCompare(b)
  )[0]
}

function normalizePartNumberKey(partNumber: string): string {
  return partNumber.replace(/\s+/g, "").trim().toUpperCase()
}

function applicationTrim(row: PdmFlatRow): string {
  return unwrapValue(
    row.application.sub_model ?? row.application.SubModel ?? ""
  )
}

function applicationBodyType(row: PdmFlatRow): string {
  return unwrapValue(
    row.application.body_type ??
      row.application.bodyType ??
      row.application.BodyType ??
      ""
  )
}

/**
 * Read every PDM application for a part number, before factory-match
 * dedupe keeps only the first trim. Used so a shared kit (Silverado 2305X
 * on Custom/LT/WT/RST) stays visible for all of those trims.
 */
export function collectTrimsByPartNumber(
  flatRows: PdmFlatRow[]
): Map<string, string[]> {
  const byPart = new Map<string, Set<string>>()
  for (const row of flatRows) {
    const partNumber = unwrapValue(
      row.product.part_number ?? row.product.PartNumber
    )
    if (!partNumber) continue
    const trim = applicationTrim(row)
    if (!trim) continue
    const key = normalizePartNumberKey(partNumber)
    const bucket = byPart.get(key) ?? new Set<string>()
    bucket.add(trim)
    byPart.set(key, bucket)
  }

  const out = new Map<string, string[]>()
  for (const [partNumber, trims] of Array.from(byPart.entries())) {
    out.set(
      partNumber,
      Array.from(trims).sort((a, b) => a.localeCompare(b))
    )
  }
  return out
}

/**
 * Read every PDM middle-row label for a part number. Used so cards can be
 * AND-filtered by selected middle after the primary slug + trim filters
 * (Suburban LT: CHE2601G vs CHE2401E).
 */
export function collectMiddlesByPartNumber(
  flatRows: PdmFlatRow[]
): Map<string, string[]> {
  const byPart = new Map<string, Set<string>>()
  for (const row of flatRows) {
    const partNumber = unwrapValue(
      row.product.part_number ?? row.product.PartNumber
    )
    if (!partNumber) continue
    const middle = getSeatAndRowSlices(row.product).middleRow.trim()
    if (!middle) continue
    const key = normalizePartNumberKey(partNumber)
    const bucket = byPart.get(key) ?? new Set<string>()
    bucket.add(middle)
    byPart.set(key, bucket)
  }

  const out = new Map<string, string[]>()
  for (const [partNumber, middles] of Array.from(byPart.entries())) {
    out.set(
      partNumber,
      Array.from(middles).sort((a, b) => a.localeCompare(b))
    )
  }
  return out
}

/**
 * Layout fingerprint (trim, body, seats, rows). Used in inspect logs so
 * you can see which SKUs share a layout. Cards are not merged by this key.
 */
export function buildFactoryMatchCollapseKey(row: PdmFlatRow): string {
  const seat = getSeatAndRowSlices(row.product)
  return [
    applicationTrim(row),
    applicationBodyType(row),
    seat.driverSeat,
    seat.passengerSeat,
    seat.frontRow,
    seat.middleRow,
    seat.backRow,
  ].join("|")
}

function resolveFactoryMatchTrimTag(
  partNumbers: string[],
  rows: PdmFlatRow[],
  trimsByPartNumber?: Map<string, string[]>
): string {
  const trims = new Set<string>()
  for (const partNumber of partNumbers) {
    const fromMap = trimsByPartNumber?.get(normalizePartNumberKey(partNumber))
    for (const trim of fromMap ?? []) {
      if (trim.trim()) trims.add(trim.trim())
    }
  }
  for (const row of rows) {
    const trim = applicationTrim(row)
    if (trim) trims.add(trim)
  }
  return Array.from(trims)
    .sort((a, b) => a.localeCompare(b))
    .join(",")
}

function resolveFactoryMatchMiddleTag(
  partNumbers: string[],
  rows: PdmFlatRow[],
  middlesByPartNumber?: Map<string, string[]>
): string {
  const middles = new Set<string>()
  for (const partNumber of partNumbers) {
    const fromMap = middlesByPartNumber?.get(normalizePartNumberKey(partNumber))
    for (const middle of fromMap ?? []) {
      if (middle.trim()) middles.add(middle.trim())
    }
  }
  for (const row of rows) {
    const middle = getSeatAndRowSlices(row.product).middleRow.trim()
    if (middle) middles.add(middle)
  }
  return Array.from(middles)
    .sort((a, b) => a.localeCompare(b))
    .join(",")
}

export function aggregateFactoryMatches(
  flatRows: PdmFlatRow[],
  slugMaps: FactoryMatchSlugMaps,
  rowOpts: { backOpts: OptionValue[]; frontOpts: OptionValue[] },
  seatDiagramLabelContext: SeatDiagramLabelContext,
  factoryMatchDatoAsset: PdmAssetRecord | null,
  trimsByPartNumber?: Map<string, string[]>,
  middlesByPartNumber?: Map<string, string[]>
): {
  factoryMatches: FactoryMatch[]
  filterConfig: FactoryMatchFilterConfig
  variation: FactoryMatchVariation
} {
  if (flatRows.length === 0) {
    console.warn("🏭 [Factory Match] No products to process")
    const emptyVariation: FactoryMatchVariation = {
      bodyTypesVary: false,
      driverSeatsVary: false,
      backRowsVary: false,
      passengerSeatsVary: false,
      trimsVary: false,
      frontRowsVary: false,
      middleRowsVary: false,
      middleRowsUniquelyIdentifyKits: false,
    }
    return {
      factoryMatches: [],
      filterConfig: resolveFactoryMatchFilterConfig(emptyVariation),
      variation: emptyVariation,
    }
  }

  const groupedByBasePart = new Map<string, PdmFlatRow[]>()

  for (const row of flatRows) {
    const partNumber = unwrapValue(
      row.product.part_number ?? row.product.PartNumber
    )

    if (!partNumber) {
      console.warn(
        "🏭 [Factory Match] Skipping product with no part_number:",
        row.product
      )
      continue
    }

    const explicitBase = unwrapValue(
      row.product.base_part_number ?? row.product.basePartNumber
    )
    const basePart = explicitBase || partNumber

    if (
      explicitBase &&
      !partNumber.toUpperCase().startsWith(explicitBase.toUpperCase())
    ) {
      console.warn(
        "🏭 [Factory Match] Skipping product - part_number does not start with base_part_number",
        { basePart: explicitBase, partNumber }
      )
      continue
    }

    if (!groupedByBasePart.has(basePart)) {
      groupedByBasePart.set(basePart, [])
    }
    groupedByBasePart.get(basePart)!.push(row)
  }

  const allContexts: FactoryMatchProductContext[] = []
  for (const [basePart, rows] of Array.from(groupedByBasePart.entries())) {
    for (const row of rows) {
      const partNumber = unwrapValue(
        row.product.part_number ?? row.product.PartNumber
      )
      if (!partNumber) continue
      allContexts.push({
        row,
        basePart,
        partNumber,
        trim: unwrapValue(
          row.application.sub_model ?? row.application.SubModel
        ),
        bodyType: unwrapValue(
          row.application.body_type ??
            row.application.bodyType ??
            row.application.BodyType
        ),
        seatInfo: getSeatAndRowSlices(row.product),
        attrs: extractFactoryMatchAttributes(row.product),
      })
    }
  }

  const variation = analyzeFactoryMatchVariation(allContexts)

  const perforationOverride = resolveFactoryMatchPerforationForVehicle(
    seatDiagramLabelContext.selectedMake,
    seatDiagramLabelContext.selectedModel
  )

  const pdmFactoryMatches: FactoryMatch[] = []
  let factoryMatchCounter = 1

  for (const [basePart, rows] of Array.from(groupedByBasePart.entries())) {
    const byPartNumber = new Map<string, PdmFlatRow[]>()
    for (const row of rows) {
      const partNumber = unwrapValue(
        row.product.part_number ?? row.product.PartNumber
      )
      if (!partNumber) continue
      if (!byPartNumber.has(partNumber)) {
        byPartNumber.set(partNumber, [])
      }
      byPartNumber.get(partNumber)!.push(row)
    }

    for (const [partNumber, partRows] of Array.from(byPartNumber.entries())) {
      if (!partNumber || !partRows.length) continue

      const factoryMatchOptions: FactoryMatchOption[] = []
      const seenTitles = new Set<string>()

      let richestAttrs = extractFactoryMatchAttributes(partRows[0].product)

      const ctx: FactoryMatchProductContext = {
        row: partRows[0],
        basePart,
        partNumber,
        trim: unwrapValue(
          partRows[0].application.sub_model ?? partRows[0].application.SubModel
        ),
        bodyType: unwrapValue(
          partRows[0].application.body_type ??
            partRows[0].application.bodyType ??
            partRows[0].application.BodyType
        ),
        seatInfo: getSeatAndRowSlices(partRows[0].product),
        attrs: richestAttrs,
      }
      const filterSlug = resolveFactoryMatchCardFilterSlug(
        factoryMatchCounter,
        ctx,
        variation,
        slugMaps,
        rowOpts.backOpts,
        rowOpts.frontOpts
      )
      const rowDisplay = resolveRowConfigDisplayLabel(
        ctx,
        filterSlug,
        rowOpts.backOpts,
        rowOpts.frontOpts
      )

      for (const row of partRows) {
        const product = row.product
        const assetDetails = extractDigitalAssetDetails(product)
        const attrs = extractFactoryMatchAttributes(product)

        if (attrs.kit || attrs.primaryColor || attrs.stitch) {
          richestAttrs = attrs
        }

        // PDM digital-assets fallback disabled — Dato images only.
        // const imageUrl = buildPdmImageUrl(assetDetails)
        const assetType = assetDetails.assetType
        const optionTitle = resolveFactoryMatchOptionTitle(
          assetType,
          assetDetails.imageFilename
        )

        const optionValue = resolveFactoryMatchOptionValue(
          assetType,
          attrs,
          product,
          row.application,
          partNumber,
          basePart,
          rowDisplay,
          seatDiagramLabelContext,
          perforationOverride
        )

        const optionRawValue = resolveFactoryMatchOptionRawValue(
          assetType,
          attrs,
          product,
          row.application,
          partNumber,
          basePart,
          rowDisplay,
          seatDiagramLabelContext,
          perforationOverride
        )

        if (!optionValue?.trim()) continue

        const titleKey = normalizeFactoryMatchTitleKey(optionTitle)
        if (seenTitles.has(titleKey)) continue
        seenTitles.add(titleKey)

        // Schematic images match the full SKU. Other options match title/value.
        const { matchValue: datoMatchValue, matchStrategy } =
          resolveDatoMatchValueForFactoryMatchOption(
            assetDetails,
            optionRawValue.trim(),
            partNumber
          )
        const datoLookup =
          matchStrategy === "sku"
            ? lookupDatoSchematicImageBySku(
                factoryMatchDatoAsset,
                datoMatchValue,
                basePart
              )
            : lookupDatoFactoryMatchImage(
                factoryMatchDatoAsset,
                optionTitle,
                datoMatchValue
              )
        // Dato images only. PDM digital-assets fallback is commented out.
        const finalImage = datoLookup.src || null
        // const finalImage =
        //   datoLookup.src || buildFactoryMatchOptionImage(optionTitle, imageUrl)

        factoryMatchOptions.push({
          id: `${partNumber}-option-${factoryMatchOptions.length + 1}`,
          title: canonicalizeFactoryMatchOptionTitle(optionTitle),
          value: optionValue.trim(),
          rawValue: optionRawValue.trim(),
          sku: partNumber,
          image: finalImage,
          keyPairs: [],
        })
      }

      const addTextOptionIfMissing = (
        title: string,
        value: string,
        rawValue: string
      ) => {
        const normalizedTitle = canonicalizeFactoryMatchOptionTitle(title)
        const normalizedValue = value.trim()
        const normalizedRawValue = rawValue.trim()
        if (!normalizedTitle || !normalizedValue) return
        const titleKey = normalizeFactoryMatchTitleKey(normalizedTitle)
        if (seenTitles.has(titleKey)) return
        seenTitles.add(titleKey)

        // The seat drawing uses the full SKU. Other tiles match title/value.
        const datoLookup = normalizedTitle
          .toLowerCase()
          .includes("seat diagram")
          ? lookupDatoSchematicImageBySku(
              factoryMatchDatoAsset,
              partNumber,
              basePart
            )
          : lookupDatoFactoryMatchImage(
              factoryMatchDatoAsset,
              normalizedTitle,
              normalizedRawValue
            )
        // Dato images only. PDM digital-assets fallback is commented out.
        const finalImage = datoLookup.src || null
        // const finalImage =
        //   datoLookup.src || buildFactoryMatchOptionImage(normalizedTitle)

        factoryMatchOptions.push({
          id: `${partNumber}-option-${factoryMatchOptions.length + 1}`,
          title: normalizedTitle,
          value: normalizedValue,
          rawValue: normalizedRawValue,
          sku: partNumber,
          image: finalImage,
          keyPairs: [],
        })
      }

      const seatDiagramLabel = formatSeatDiagramSchematicLabel(
        partRows[0].application,
        unwrapValue(partRows[0].product.title ?? partRows[0].product.Title),
        partRows[0].product,
        rowDisplay,
        seatDiagramLabelContext
      )

      addTextOptionIfMissing(
        "Seat diagram schematic",
        seatDiagramLabel,
        seatDiagramLabel // Same for raw value
      )

      const kitValue = resolveFactoryMatchKitDisplayValue(partNumber, basePart)
      addTextOptionIfMissing(
        "Kit",
        kitValue,
        richestAttrs.kit || kitValue // Use raw kit attribute or kitValue
      )

      const patternValue = richestAttrs.pattern || FACTORY_MATCH_PATTERN_DEFAULT
      addTextOptionIfMissing(
        "Pattern",
        patternValue,
        patternValue // Same for raw value (pattern not transformed)
      )

      const toneValue = richestAttrs.tone || FACTORY_MATCH_TONE_DEFAULT
      addTextOptionIfMissing(
        "Tone",
        toneValue,
        toneValue // Same for raw value (tone not transformed)
      )

      const catalogPerforation =
        perforationOverride === null || perforationOverride === undefined
          ? ""
          : perforationOverride.trim()
      const perforationValue = richestAttrs.perforation || catalogPerforation
      if (perforationValue) {
        addTextOptionIfMissing(
          "Perforation",
          perforationValue,
          perforationValue // Same for raw value (perforation not transformed)
        )
      }

      addTextOptionIfMissing(
        "Primary color",
        richestAttrs.primaryColor,
        richestAttrs.primaryColor // Use raw primary color attribute
      )

      addTextOptionIfMissing(
        "Insert color",
        richestAttrs.insertColor,
        richestAttrs.insertColor // Use raw insert color attribute
      )

      addTextOptionIfMissing(
        "Stitch",
        richestAttrs.stitch,
        richestAttrs.stitch // Use raw stitch attribute
      )

      const sortedOptions =
        sortFactoryMatchOptionsByDisplayOrder(factoryMatchOptions)

      if (sortedOptions.length === 0) {
        console.warn(
          `🏭 [Factory Match] Skipping SKU ${partNumber} - no valid options`
        )
        continue
      }

      ctx.attrs = richestAttrs

      // Card header shows the PDM part number (SKU) only — not trim/middle labels.
      const keyPairs: KeyPair[] = [
        {
          id: `${partNumber}-keypair`,
          key: partNumber,
          values: filterSlug,
        },
      ]
      const trimTag = resolveFactoryMatchTrimTag(
        [partNumber],
        partRows,
        trimsByPartNumber
      )
      if (trimTag) {
        keyPairs.push({
          id: `${partNumber}-trim`,
          key: FACTORY_MATCH_TRIM_KEYPAIR_KEY,
          values: trimTag,
        })
      }
      const middleTag = resolveFactoryMatchMiddleTag(
        [partNumber],
        partRows,
        middlesByPartNumber
      )
      if (middleTag) {
        keyPairs.push({
          id: `${partNumber}-middle`,
          key: FACTORY_MATCH_MIDDLE_KEYPAIR_KEY,
          values: middleTag,
        })
      }

      const firstImageOption = sortedOptions.find((opt) => opt.image)

      pdmFactoryMatches.push({
        id: `pdm-factory-match-${factoryMatchCounter}`,
        sku: partNumber,
        keyPairs,
        factoryMatchOption: sortedOptions,
        image: firstImageOption?.image || null,
      })

      factoryMatchCounter++
    }
  }

  const filterConfig = resolveFactoryMatchFilterConfig(variation)

  return {
    factoryMatches: pdmFactoryMatches,
    filterConfig,
    variation,
  }
}

/**
 * Public entry: Build steps with PDM data for body/rows/factory-match.
 *
 * ## PDM-First Architecture
 *
 * This function builds Body, Rows, and Factory Match steps INDEPENDENTLY from Dato:
 * - PDM steps are ALWAYS included in the result (not conditional on Dato)
 * - PDM step order is defined in this code (not from Dato)
 * - PDM step structure comes from step-structure.ts constants
 * - PDM option values come from products API
 *
 * The `datoSteps` parameter is used ONLY for:
 * - Non-PDM steps (pattern, design, material, etc.)
 * - These are filtered and inserted between PDM steps
 *
 * Final step order (guaranteed):
 * 1. body (PDM)
 * 2. rows (PDM)
 * 3. pattern/design/material/etc. (from datoSteps, if present)
 * 4. factory-match (PDM)
 */
